async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

export async function fetchMarketRegions({ accessToken, countryCode = "" }) {
  const params = new URLSearchParams();

  const normalizedCountry = String(countryCode || "")
    .trim()
    .toUpperCase();

  if (normalizedCountry) {
    params.set("country_code", normalizedCountry);
  }

  // Homebuyer dossier creation only exposes operational markets.
  params.set("require_lender", "true");

  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/market-regions/${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        ...(accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : {}),
        Accept: "application/json",
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Could not load market regions.",
    );
  }

  if (Array.isArray(data)) {
    return data;
  }

  return Array.isArray(data?.results) ? data.results : [];
}

export async function initializeDossierFromPersonalInfo({
  accessToken,
  countryOfPropertyPurchase,
  marketRegionId,
  personalInfo,
}) {
  const res = await fetch(`${API_BASE}/api/homeowner/dossiers/initialize/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      country_of_property_purchase: String(countryOfPropertyPurchase || "")
        .trim()
        .toUpperCase(),
      market_region_id: Number(marketRegionId),
      borrowers: personalInfo?.borrowers || [],
    }),
  });

  const data = await safeJson(res);

  if (res.status === 409 && data?.dossier_id) {
    return {
      ...data,
      already_exists: true,
    };
  }

  if (!res.ok) {
    throw new Error(
      data?.error || data?.detail || "Could not initialize dossier.",
    );
  }

  return data || {};
}

export async function deleteDossier({ dossierId, accessToken }) {
  const response = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/delete/`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    let message = "Could not delete dossier.";

    try {
      const data = await response.json();
      message = data?.detail || data?.error || message;
    } catch {
      // Keep fallback message.
    }

    throw new Error(message);
  }

  return true;
}

export async function updateDossierPersonalInfo({
  accessToken,
  dossierId,
  countryOfPropertyPurchase,
  borrowers,
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/personal-info/`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
      body: JSON.stringify({
        country_of_property_purchase: String(countryOfPropertyPurchase || "")
          .trim()
          .toUpperCase(),
        borrowers: borrowers || [],
      }),
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to update personal information.",
    );
  }

  return data || {};
}

export async function getDossierDetail({ accessToken, dossierId }) {
  const res = await fetch(`${API_BASE}/api/homeowner/dossiers/${dossierId}/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Could not load the dossier.",
    );
  }

  return data;
}

export async function downloadDossierZip({
  dossierId,
  accessToken,
  lang = "en",
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/download/?lang=${encodeURIComponent(lang)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "*/*",
      },
    },
  );

  if (!res.ok) {
    throw new Error("Could not download dossier.");
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `legup-dossier-${dossierId}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function updateDossierFinancialInfo({
  accessToken,
  dossierId,
  borrowers,
  dossierFinancialInfo,
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/financial-info/`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        borrowers: borrowers || [],
        dossier_financial_info: dossierFinancialInfo || {},
      }),
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to update financial information.",
    );
  }

  return data;
}

export async function updateDossierPropertyInfo({
  accessToken,
  dossierId,
  propertyInfo,
  dossierFinancialInfo,
}) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/property-info/`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        property_information: propertyInfo || {},
        dossier_financial_info: dossierFinancialInfo || {},
      }),
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Failed to update property information.",
    );
  }

  return data;
}

export async function prepareDossierDownload({ dossierId, accessToken }) {
  const res = await fetch(
    `${API_BASE}/api/homeowner/dossiers/${dossierId}/prepare-download/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(
      data?.detail || data?.error || "Could not prepare dossier.",
    );
  }

  return data;
}

export async function fetchParticipatingLenders({
  accessToken,
  marketRegionId,
}) {
  const params = new URLSearchParams();

  if (marketRegionId) {
    params.set("market_region_id", String(marketRegionId));
  }

  const query = params.toString();

  const res = await fetch(
    `${API_BASE}/api/lenders/participating/${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
  );

  const data = await safeJson(res);

  if (!res.ok) {
    throw new Error(data?.detail || "Failed to fetch participating lenders.");
  }

  if (Array.isArray(data)) {
    return data;
  }

  return Array.isArray(data?.lenders) ? data.lenders : [];
}
